import React, {useState, useMemo, useEffect} from 'react';
import {Stack, Typography, Button, Box, Grid2, Divider, Tab, Tabs} from "@mui/material";
import ReportsBackIcon from "../../../../assets/icons/reports-back-icon";
import {useNavigate, useOutletContext} from "react-router-dom";
import {GroupService} from "../../../../services/groups.service";
import {UserActivityFilters} from "../../../../components/user-activity-filters";
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {UserActivityService} from "../../../../services/user-activity.service";
import {ActiveUserPerTeamTable} from "../../../../components/active-user-per-team-table";
import {ActiveUserPercentPerTeamTable} from "../../../../components/active-user-percent-per-team-table";
import HorizontalBarChart from "../../../../components/horizontal-bar-chart";
import LineChart from "../../../../components/line-chart";
import {UserActivityTypes} from "../../../../types";
import {When} from "react-if";
import Loading from "../../../../components/loading";
import dayjs from "dayjs";
import {Team} from "../../../../types/teams.types";
import {TeamActivity} from "./team-activity";

const styles = {
    container: {
        borderRadius: 1,
        border: '0.1px solid #ccc',
        backgroundColor: 'white',
        padding: '3rem 1.5rem 3rem 1.5rem'
    }
};


export const UserActivity: React.FC = () => {

    interface ContextType {
        setReportTitle: (title: string) => void;
        setReportDescText: (desc: string) => void;
    }

    const { setReportTitle, setReportDescText } = useOutletContext<ContextType>();
    const [viewReport, setViewReport] = useState<boolean>(false);
    const [allTeams, setAllTeams] = useState<Team[]>([]);
    const [showTeamActivity, setShowTeamActivity] = useState<string>('false');
    const [fetchDataOnTeamActivity, setFetchDataOnTeamActivity] = useState<boolean>(false);

    const initStartDate = dayjs().subtract(7, 'day').startOf('day')
    const initEndDate = dayjs().endOf('day')

    const initFilter = {
        startDate : initStartDate?.format('YYYY-MM-DD HH:MM'),
        endDate : initEndDate?.format('YYYY-MM-DD HH:MM'),
        teamIds : [],
        groupId : null
    } as UserActivityTypes.UserActivityFilter;

    const [filter, setFilter] = useState<typeof initFilter>(initFilter);
    const [teamActivityFilter, setTAFilter] = useState<typeof initFilter>(initFilter);

    const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
    const [isUnassignedTeamSelected, setIsUnassignedTeamSelected] = useState<boolean>(false);

    const activeUserTableSortInit : {sortBy : string, sortOrder : 'ASC' | 'DESC'} = {
        sortBy: 'activePercentage',
        sortOrder: 'DESC'
    }

    const teamActivitySortInit : {sortBy : string, sortOrder : 'ASC' | 'DESC'} = {
        sortBy: 'username',
        sortOrder: 'ASC'
    }

    const [activeUserTableSort, setActiveUserTableSort] = useState<typeof activeUserTableSortInit>(activeUserTableSortInit);
    const [weeklyActiveTableSort, setWeeklyActiveTableSort] = useState<typeof activeUserTableSortInit>(activeUserTableSortInit);
    const [teamUserTableSort, setTeamUserTableSort] = useState<typeof teamActivitySortInit>(teamActivitySortInit);
    const [teamUserWeeklyTableSort, setTeamUserWeeklyTableSort] = useState<typeof teamActivitySortInit>(teamActivitySortInit);

    const {
        activeUserRecords,
        weeklyActiveUserRecords,
        loading,
    } = useSelector((state: RootState) => state.userActivity);

    const {
        items: allGroups,
        loading : loadingGroups,
    } = useSelector((state: RootState) => state.groups);

    //fetch groups and teams
    useEffect(()=>{
        GroupService.fetchAll(() => {});
    },[])

    //set teamIds once groups are fetched
    useEffect(() => {
        let allTeams = allGroups?.map((group) => group?.teams || []).flat() as Team[];
        setAllTeams(allTeams)
        let teamIds = allTeams?.map((team) => team.id) || [];
        if(teamIds?.length){
            setFilter({...filter,teamIds})
            setTAFilter({...teamActivityFilter,teamIds: [...teamIds, 'Unassigned']})
        }
    }, [allGroups]);

    useEffect(()=>{
        if(filter.teamIds?.length){
            UserActivityService.fetchUserActivityData(filter, activeUserTableSort.sortBy, activeUserTableSort.sortOrder)
            UserActivityService.fetchWeeklyActivityData(filter, activeUserTableSort.sortBy, activeUserTableSort.sortOrder)
        }
    },[filter])

    useEffect(()=>{
        if(filter.teamIds?.length){
            UserActivityService.fetchUserActivityData(filter, activeUserTableSort.sortBy, activeUserTableSort.sortOrder)
        }
    },[activeUserTableSort])

    useEffect(()=>{
        if(filter.teamIds?.length){
            UserActivityService.fetchWeeklyActivityData(filter, weeklyActiveTableSort.sortBy, weeklyActiveTableSort.sortOrder)
        }
    },[weeklyActiveTableSort])

   useEffect(() => {
        if (showTeamActivity === 'true') {
            let teamId = getTeamIdForTeamUserData();
            UserActivityService.fetchTeamUsersActivity(teamId!, teamActivityFilter, teamUserTableSort.sortBy, teamUserTableSort.sortOrder);
            UserActivityService.fetchTeamUsersWeeklyActivity(teamId!, teamActivityFilter, teamUserWeeklyTableSort.sortBy, teamUserWeeklyTableSort.sortOrder);
        }
    }, [selectedTeam, teamActivityFilter, fetchDataOnTeamActivity]);


    useEffect(()=>{
        if (showTeamActivity === 'true') {
            let teamId = getTeamIdForTeamUserData();
            UserActivityService.fetchTeamUsersActivity(teamId!, teamActivityFilter, teamUserTableSort.sortBy ,teamUserTableSort.sortOrder);
        }
    },[teamUserTableSort])

    useEffect(()=>{
        if (showTeamActivity === 'true') {
            let teamId = getTeamIdForTeamUserData();
            UserActivityService.fetchTeamUsersWeeklyActivity(teamId!, teamActivityFilter, teamUserWeeklyTableSort.sortBy ,teamUserWeeklyTableSort.sortOrder);
        }
    },[teamUserWeeklyTableSort])

    useMemo(() => {
        setReportTitle("Groups & Teams Activity");
        setReportDescText("Monitor how your groups and teams are using the platform.");
    }, []);

    const navigate = useNavigate();

    const handleBack = () => {
        if(viewReport)
            setViewReport(false);
        else
            navigate('/reports');
    }

    const onFilter = async (filters: UserActivityTypes.UserActivityFilter) => {
        setSelectedTeam(null)
        setFilter({...filter,...filters});
    }

    const activeUserTableOnSort = (sortBy: string, sortOrder : 'ASC' | 'DESC') => {
        setActiveUserTableSort({ sortBy, sortOrder });
    }

    const weeklyActiveTableOnSort = (sortBy: string, sortOrder : 'ASC' | 'DESC') => {
        setWeeklyActiveTableSort({ sortBy, sortOrder });
    }

    const teamUserActivityTableOnSort = (sortBy: string, sortOrder : 'ASC' | 'DESC') => {
        setTeamUserTableSort({ sortBy, sortOrder });
    }

    const teamWeeklyActivityTableOnSort = (sortBy: string, sortOrder : 'ASC' | 'DESC') => {
        setTeamUserWeeklyTableSort({ sortBy, sortOrder });
    }

    const onTeamActivityFilter = async (filters: UserActivityTypes.UserActivityFilter) => {
        setSelectedTeam(null)
        setTAFilter({...teamActivityFilter,...filters});
    }

   const onTableRowClick = (teamId: string) => {
        let team: Team | null = allTeams.find((t) => t.id === teamId) ?? null;
        if (team || teamId === '-1') {
            if(teamId === '-1')
                setIsUnassignedTeamSelected(true)
            else
                setIsUnassignedTeamSelected(false)

            setSelectedTeam(team);
            setShowTeamActivity('true');
        }
    }

    const getTeamIdForTeamUserData = () => {
        let teamId: string | string[] | null = null;
        if (isUnassignedTeamSelected && !selectedTeam) {
            teamId = 'Unassigned';
        } else if (selectedTeam) {
            teamId = selectedTeam.id!;
        } else if (teamActivityFilter.teamIds?.length > 0) {
            teamId = teamActivityFilter.teamIds;
        }
        return teamId
    }

    //sort activeUserRecords by teamId where unassigned teamId is -1
    const sortedActiveUserRecords = useMemo(() => {
        if (!activeUserRecords) return [];
        // Sort by teamName alphabetically, but put "No Team Assigned" (teamId === '-1') at the end
        return [...activeUserRecords].sort((a, b) => {
            if (a.teamId === '-1') return 1;
            if (b.teamId === '-1') return -1;
            return 0;
        });
    }, [activeUserRecords]);

    return (
        <Stack>
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                justifyContent: 'left',
                textAlign: 'left',
                marginTop: 4,
                marginBottom: 6
            }}>
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleBack}
                    disabled={false}
                    sx={{
                        border : 0,
                        padding:1,
                        boxShadow : '0 0 2px 0 rgba(145, 158, 171, 0.24),0 16px 32px -4px rgba(145, 158, 171, 0.24)'
                    }}
                >
                    <ReportsBackIcon/>
                    <Typography fontWeight={400} fontSize={14} color={'black'} marginLeft={1}>
                        Back
                    </Typography>
                </Button>
            </Box>
            <Stack>
                <Tabs value={showTeamActivity} onChange={(e, v) => {
                    setShowTeamActivity(v);
                    setFetchDataOnTeamActivity(!!v)
                }}>
                    <Tab label="All Activity" value={'false'} title="View activities of all teams and groups"/>
                    <Tab label="Team Activity" value={'true'} title="View activities of selected team" disabled={false}/>
                </Tabs>
                <When condition={loadingGroups?.fetchAll}>
                    <Loading />
                </When>
                <When condition={!loadingGroups?.fetchAll}>
                        <Stack spacing={2} mt={2} sx={{ display: showTeamActivity === 'false' ? 'block' : 'none' }}>
                            <UserActivityFilters
                                allGroups={allGroups}
                                onFilter={onFilter}
                            />
                        </Stack>
                        <Stack spacing={2} mt={2} sx={{ display: showTeamActivity === 'true' ? 'block' : 'none' }}>
                            <UserActivityFilters
                                allGroups={allGroups}
                                onFilter={onTeamActivityFilter}
                            />
                        </Stack>
                    <When condition={showTeamActivity === 'false'}>
                        <Stack mt={6}>
                            <Grid2 container spacing={5}>
                                <Grid2 sx={{...styles.container}} size={{md:6,xs:12,sm:12}}>
                                    <ActiveUserPerTeamTable loading = {loading?.fetchActiveUserData} onRowClick={onTableRowClick} onSort={activeUserTableOnSort} currentSortBy={activeUserTableSort.sortBy} currentSortOrder={activeUserTableSort.sortOrder} data={sortedActiveUserRecords} />
                                </Grid2>
                                <Grid2 sx={{...styles.container}} size={{md:6,xs:12,sm:12}}>
                                    <When condition={loading?.fetchActiveUserData}>
                                        <Loading />
                                    </When>
                                    <When condition={!loading?.fetchActiveUserData}>
                                        <HorizontalBarChart data={sortedActiveUserRecords} onRowClick={onTableRowClick}/>
                                    </When>
                                </Grid2>
                                <Grid2 sx={{...styles.container}} size={{md:6,xs:12,sm:12}}>
                                    <When condition={!loading?.fetchWeeklyActiveData}>
                                        <LineChart data={weeklyActiveUserRecords} />
                                    </When>
                                    <When condition={loading?.fetchWeeklyActiveData}>
                                        <Loading />
                                    </When>
                                </Grid2>
                            </Grid2>
                            <Divider sx={{ mt: 3, mb : 3}} />
                            <Grid2 container spacing={2}>
                                <Grid2 size={{md:10,xs:12,sm:12}}>
                                    <ActiveUserPercentPerTeamTable loading={loading?.fetchWeeklyActiveData} onSort={weeklyActiveTableOnSort} currentSortOrder={weeklyActiveTableSort.sortOrder} currentSortBy={weeklyActiveTableSort.sortBy} data={weeklyActiveUserRecords} allTeams={allTeams}/>
                                </Grid2>
                            </Grid2>
                        </Stack>
                    </When>
                    <When condition={showTeamActivity === 'true'}>
                        <TeamActivity
                            onSort = {teamUserActivityTableOnSort}
                            currentSortOrder={teamUserTableSort.sortOrder}
                            currentSortBy={teamUserTableSort.sortBy}
                            onWeeklySort = {teamWeeklyActivityTableOnSort}
                            currentWeeklySortOrder={teamUserWeeklyTableSort.sortOrder}
                            currentWeeklySortBy={teamUserWeeklyTableSort.sortBy}

                        />
                    </When>
                </When>
            </Stack>
        </Stack>
    )
}
